/**
 * PLAYER module — center-screen interaction raycast against meshes WORLD tags
 * with `userData.interactId`, InteractTarget construction, and the hover
 * highlight (a PLAYER-owned Box3Helper outline; the frozen EventMap has no
 * highlight event, so highlight state is visual + `userData.hovered`).
 * Owned by PLAYER; see CONTRACTS.md.
 */
import { Box3, Box3Helper, Color, Raycaster, Vector2, Vector3 } from 'three'
import type { Camera, Object3D, Scene } from 'three'

import type { InteractKind, InteractTarget } from '../core/types'
import {
  DEFAULT_INTERACT_RADIUS,
  HIGHLIGHT_COLOR,
  INTERACT_MAX_DISTANCE
} from './constants'

const SCREEN_CENTER = new Vector2(0, 0)

const KIND_BY_PREFIX: Readonly<Record<string, InteractKind>> = {
  clue: 'clue',
  door: 'door',
  suspect: 'suspect',
  accuse: 'suspect',
  prop: 'prop'
}

const PROMPT_BY_KIND: Readonly<Record<InteractKind, string>> = {
  clue: 'Examine',
  door: 'Open door',
  suspect: 'Question',
  prop: 'Inspect'
}

const isInteractKind = (value: unknown): value is InteractKind =>
  value === 'clue' || value === 'door' || value === 'suspect' || value === 'prop'

/** WORLD tags prompts as `interactPrompt`; honor plain `prompt` too. */
const readPrompt = (data: Record<string, unknown>): string | null => {
  if (typeof data.interactPrompt === 'string') return data.interactPrompt
  if (typeof data.prompt === 'string') return data.prompt
  return null
}

/** All scene objects carrying the contract's `userData.interactId` tag. */
export const collectInteractables = (scene: Scene): readonly Object3D[] => {
  const list: Object3D[] = []
  scene.traverse((object) => {
    if (typeof object.userData.interactId === 'string') {
      list.push(object)
    }
  })
  return list
}

/**
 * Build a full InteractTarget from a tagged object. Only `interactId` is
 * contractual; optional richer userData (kind/prompt/clueId/suspectId/
 * interactRadius) is honored when present, with `<kind>:<rest>` id-prefix
 * parsing as the fallback.
 */
export const buildInteractTarget = (object: Object3D): InteractTarget => {
  const data = object.userData
  const id = String(data.interactId)
  const separator = id.indexOf(':')
  const prefix = separator > 0 ? id.slice(0, separator) : ''
  const rest = separator > 0 ? id.slice(separator + 1) : id

  const dataKind = data.interactKind ?? data.kind
  const kind: InteractKind = isInteractKind(dataKind)
    ? dataKind
    : (KIND_BY_PREFIX[prefix] ?? 'prop')

  const worldPosition = object.getWorldPosition(new Vector3())
  const clueId = typeof data.clueId === 'string' ? data.clueId : rest
  const suspectId = typeof data.suspectId === 'string' ? data.suspectId : rest

  return {
    id,
    kind,
    position: { x: worldPosition.x, y: worldPosition.y, z: worldPosition.z },
    radius: typeof data.interactRadius === 'number' ? data.interactRadius : DEFAULT_INTERACT_RADIUS,
    prompt: readPrompt(data) ?? PROMPT_BY_KIND[kind],
    ...(kind === 'clue' ? { clueId } : {}),
    ...(kind === 'suspect' ? { suspectId } : {})
  }
}

const findInteractOwner = (hitObject: Object3D): Object3D | null => {
  let current: Object3D | null = hitObject
  while (current) {
    if (typeof current.userData.interactId === 'string') return current
    current = current.parent
  }
  return null
}

export interface InteractionProbe {
  /** Raycast from screen center; returns the hovered tagged object, if any. */
  probe(camera: Camera, interactables: readonly Object3D[]): Object3D | null
}

export const createInteractionProbe = (): InteractionProbe => {
  const raycaster = new Raycaster()
  raycaster.far = INTERACT_MAX_DISTANCE

  return {
    probe: (camera, interactables) => {
      if (interactables.length === 0) return null
      raycaster.setFromCamera(SCREEN_CENTER, camera)
      const hits = raycaster.intersectObjects([...interactables], true)
      for (const hit of hits) {
        const owner = findInteractOwner(hit.object)
        if (owner) return owner
      }
      return null
    }
  }
}

export interface HighlightHandle {
  /** Update which object is highlighted; pass null to clear. */
  set(object: Object3D | null): void
  current(): Object3D | null
}

/** PLAYER-owned wireframe outline around the hovered interactable. */
export const createHighlight = (scene: Scene): HighlightHandle => {
  const bounds = new Box3()
  const helper = new Box3Helper(bounds, new Color(HIGHLIGHT_COLOR))
  helper.visible = false
  scene.add(helper)

  let hovered: Object3D | null = null

  const set = (object: Object3D | null): void => {
    if (object === hovered) {
      if (object) bounds.setFromObject(object)
      return
    }
    if (hovered) {
      hovered.userData.hovered = false
    }
    hovered = object
    if (object) {
      object.userData.hovered = true
      bounds.setFromObject(object)
      helper.visible = true
    } else {
      helper.visible = false
    }
  }

  return { set, current: () => hovered }
}
